import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const THEME = {
  colors: {
    primary: '#FF6B6B',
    secondary: '#4ECDC4',
    accent1: '#FFE66D',
    accent2: '#96CEB4',
    accent3: '#FFEEAD',
    accent4: '#FF9999',
    text: '#363636',
    background: '#FFFFFF',
    mainBackground: '#FFF5F5'
  }
};

interface InfoDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const InfoDialog: React.FC<InfoDialogProps> = ({ open, onClose, title, children }) => {
  const muiTheme = useTheme();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: '85vh',
          borderRadius: 3,
          bgcolor: THEME.colors.mainBackground,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            backgroundImage: `
              linear-gradient(
                ${THEME.colors.secondary}30 2px,
                transparent 2px
              ),
              linear-gradient(
                90deg,
                ${THEME.colors.secondary}30 2px,
                transparent 2px
              )`,
            backgroundSize: '40px 40px',
            backgroundPosition: '0px 0px',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 245, 245, 0.6)',
            pointerEvents: 'none',
            zIndex: 0,
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: { xs: '10px 12px', sm: '16px 24px' },
        pr: { xs: '40px', sm: '56px' },
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        position: 'relative',
        zIndex: 1,
        bgcolor: 'rgba(255, 255, 255, 0.8)'
      }}> 
        <Typography 
          variant="h6" 
          component="div"
          sx={{
            color: THEME.colors.primary,
            fontWeight: 600,
            fontSize: { xs: '1.0rem', sm: '1.25rem' }
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: 6, sm: 12 },
            top: { xs: 6, sm: 12 },
            color: (theme) => theme.palette.grey[500],
            '&:hover': {
              color: THEME.colors.primary,
              backgroundColor: 'rgba(255, 107, 107, 0.08)'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }} />
        </IconButton>
      </DialogTitle>
      <DialogContent 
        sx={{ 
          px: { xs: '12px', sm: 3 },
          py: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1,
          boxShadow: 'inset 0 0 10px rgba(0,0,0,0.03)',
          bgcolor: '#FFFFFF'
        }}
      > 
        {children}
      </DialogContent>
      <DialogActions sx={{ 
        p: { xs: '8px', sm: 2 },
        borderTop: `1px solid ${muiTheme.palette.divider}`,
        position: 'relative',
        zIndex: 1,
        bgcolor: '#FFFFFF'
      }}> 
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ 
            bgcolor: THEME.colors.secondary,
            color: 'white',
            textTransform: 'none',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            px: { xs: 1.5, sm: 3 },
            py: { xs: 0.5, sm: 1 },
            '&:hover': {
              bgcolor: THEME.colors.secondary,
              opacity: 0.9
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoDialog; 